/**
 * Course outline — sections, subsections and units.
 *
 * This page exists because Verawood has no other one. Studio's own outline template was
 * removed when authoring moved to a micro-frontend, so on a headless install "create a
 * course" otherwise leads to a dead end: the course exists and there is nowhere to put
 * content in it.
 *
 * Every write goes through our CMS wrapper around Studio's `handle_xblock`, the same
 * service function Studio's own view calls, so permissions, publishing semantics and
 * validation are the platform's rather than ours.
 *
 * One deliberate choice about freshness: after any write the whole outline is refetched
 * rather than patched locally. A publish changes the state of ancestors and descendants
 * at once — a section going live can flip its parent from `needs_attention` to `live` —
 * and reproducing that rollup in the client would be a second implementation of the
 * platform's rules, guaranteed to drift.
 */

import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Banner,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Icon,
  Kpi,
  KpiRow,
  Spinner,
} from '../../shared/ui';
import {
  addBlock,
  deleteBlock,
  discardChanges,
  duplicateBlock,
  getOutline,
  publishBlock,
  renameBlock,
  reorderChildren,
  updateBlockSettings,
  type BlockSettings,
  type OutlineBlock,
} from '../../shared/lib/studio';
import { PageHeader } from '../AdminShell';
import { OutlineNode, type DragState, type OutlineOps } from '../outline/OutlineNode';
import { ConfigureDialog } from '../outline/ConfigureDialog';
import { LibraryUpdates } from '../outline/LibraryUpdates';
import '../outline/outline.css';

/** Walk the tree. Used to resolve a parent's current child order before reordering. */
function findBlock(root: OutlineBlock, locator: string): OutlineBlock | null {
  if (root.id === locator) return root;
  for (const child of root.child_info?.children ?? []) {
    const found = findBlock(child, locator);
    if (found) return found;
  }
  return null;
}

function countByCategory(root: OutlineBlock, counts: Record<string, number> = {}) {
  for (const child of root.child_info?.children ?? []) {
    counts[child.category] = (counts[child.category] ?? 0) + 1;
    countByCategory(child, counts);
  }
  return counts;
}

function countUnpublished(root: OutlineBlock, total = 0): number {
  for (const child of root.child_info?.children ?? []) {
    if (child.has_changes) total += 1;
    total = countUnpublished(child, total);
  }
  return total;
}

type Confirm =
  | { kind: 'delete'; block: OutlineBlock; label: string }
  | { kind: 'discard'; block: OutlineBlock };

export default function CourseOutline() {
  const { courseId = '' } = useParams();
  const queryClient = useQueryClient();

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [autoExpanded, setAutoExpanded] = useState(false);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [configuring, setConfiguring] = useState<{ block: OutlineBlock; label: string } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const outline = useQuery({
    queryKey: ['outline', courseId],
    queryFn: () => getOutline(courseId),
    enabled: Boolean(courseId),
  });

  const root = outline.data?.course_structure;
  const sections = root?.child_info?.children ?? [];

  // Sections open on arrival, deeper levels closed: the shape of the course should be
  // legible immediately, but a course with forty units should not open as a wall of text.
  // Runs once, so it does not fight the author's own collapsing on every refetch.
  if (root && !autoExpanded && sections.length > 0) {
    setAutoExpanded(true);
    setExpanded(new Set(sections.map((section) => section.id)));
  }

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['outline', courseId] });

  /**
   * One mutation for every block operation.
   *
   * `locator` rides along so exactly one row can show as busy, and the message is
   * supplied per call because "could not reorder" and "could not delete" need different
   * reassurance about what state the course is now in.
   */
  const act = useMutation({
    mutationFn: (op: { locator: string; run: () => Promise<unknown>; failure: string }) => op.run(),
    onMutate: () => setFailure(null),
    onSuccess: () => {
      invalidate();
      setConfirm(null);
      setConfiguring(null);
    },
    onError: (_error, op) => setFailure(op.failure),
  });

  const run = (locator: string, failure: string, fn: () => Promise<unknown>) =>
    act.mutate({ locator, failure, run: fn });

  const ops: OutlineOps = {
    expanded,
    toggle: (locator) =>
      setExpanded((current) => {
        const next = new Set(current);
        if (next.has(locator)) next.delete(locator);
        else next.add(locator);
        return next;
      }),
    rename: (locator, name) =>
      run(locator, 'The new name was not saved. The old one is still in place.', () =>
        renameBlock(locator, name),
      ),
    addChild: (parentLocator, category, label) =>
      run(parentLocator, `The new ${label.toLowerCase()} was not created.`, async () => {
        const created = await addBlock(parentLocator, category, `New ${label.toLowerCase()}`);
        // Open the parent so the new child is visible rather than hidden behind a
        // collapsed row the author then has to hunt for.
        setExpanded((current) => new Set(current).add(parentLocator));
        return created;
      }),
    publish: (locator) =>
      run(locator, 'Publishing failed. Nothing was published, so no learner saw a partial change.', () =>
        publishBlock(locator),
      ),
    discard: (block) => setConfirm({ kind: 'discard', block }),
    duplicate: (parentLocator, locator) =>
      run(locator, 'The copy was not created.', () => duplicateBlock(parentLocator, locator)),
    remove: (block, label) => setConfirm({ kind: 'delete', block, label }),
    configure: (block, label) => setConfiguring({ block, label }),
    move: (parentLocator, from, to) => {
      if (!root) return;
      const parent = findBlock(root, parentLocator);
      const children = parent?.child_info?.children;
      if (!children || to < 0 || to >= children.length) return;

      // Studio's contract is the full child list in its new order, so the reorder is
      // computed here as a splice rather than sent as a from/to pair.
      const order = children.map((child) => child.id);
      const [moved] = order.splice(from, 1);
      if (moved === undefined) return;
      order.splice(to, 0, moved);

      run(moved, 'The order was not changed. The outline is still as it was.', () =>
        reorderChildren(parentLocator, order),
      );
    },
    pending: act.isPending ? act.variables?.locator ?? null : null,
    drag,
    setDrag,
    unitHref: (locator) => `/admin/courses/${encodeURIComponent(courseId)}/units/${encodeURIComponent(locator)}`,
  };

  const counts = useMemo(() => (root ? countByCategory(root) : {}), [root]);
  const unpublished = useMemo(() => (root ? countUnpublished(root) : 0), [root]);
  const childInfo = root?.child_info;

  if (!courseId) {
    return <Banner tone="danger">No course was named in the address.</Banner>;
  }

  if (outline.isLoading) return <Spinner label="Loading the outline…" />;

  if (outline.isError || !root) {
    return (
      <>
        <PageHeader title="Course outline" />
        <Banner tone="danger">
          <strong>Could not load this course.</strong> Either it does not exist or you do not
          have access to it. Nothing has changed — go back to Courses and try again.
        </Banner>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={root.display_name}
        description={courseId}
        actions={
          <>
            {root.has_changes && (
              <Button
                variant="primary"
                busy={ops.pending === root.id}
                onClick={() =>
                  run(root.id, 'Publishing failed. Nothing was published.', () =>
                    publishBlock(root.id),
                  )
                }
              >
                <Icon name="publish" />
                Publish all changes
              </Button>
            )}
            {outline.data?.lms_link && (
              <a className="ui-btn" data-variant="ghost" href={outline.data.lms_link}>
                <Icon name="open_in_new" />
                View as learner
              </a>
            )}
          </>
        }
      />

      {failure && (
        <Banner tone="danger">
          <strong>{failure}</strong> If it keeps happening, reload the page to be sure of what
          is saved.
        </Banner>
      )}

      {/* Above the KPIs: an update waiting on a person outranks a count. */}
      <LibraryUpdates courseKey={courseId} />

      <KpiRow>
        <Kpi label={plural(childInfo?.display_name ?? 'Section', sections.length)} value={sections.length} />
        <Kpi label={plural('Subsection', counts['sequential'] ?? 0)} value={counts['sequential'] ?? 0} />
        <Kpi label={plural('Unit', counts['vertical'] ?? 0)} value={counts['vertical'] ?? 0} />
        <Kpi
          label="With draft changes"
          value={unpublished}
          hint={unpublished > 0 ? 'Not visible to learners yet' : 'Everything is published'}
        />
      </KpiRow>

      <Card>
        {sections.length === 0 ? (
          <EmptyState
            title="Nothing in this course yet"
            body={`Courses are built from ${(childInfo?.display_name ?? 'section').toLowerCase()}s, each holding subsections, each holding units of content.`}
            action={
              childInfo && (
                <Button
                  variant="primary"
                  busy={ops.pending === root.id}
                  onClick={() => ops.addChild(root.id, childInfo.category, childInfo.display_name)}
                >
                  <Icon name="add" />
                  Add the first {childInfo.display_name.toLowerCase()}
                </Button>
              )
            }
          />
        ) : (
          <>
            <div className="outline-toolbar">
              <button
                type="button"
                className="ui-btn"
                data-variant="ghost"
                onClick={() => setExpanded(new Set(allLocators(root)))}
              >
                Expand all
              </button>
              <button
                type="button"
                className="ui-btn"
                data-variant="ghost"
                onClick={() => setExpanded(new Set())}
              >
                Collapse all
              </button>
              {unpublished > 0 && (
                <Badge tone="warning">
                  {unpublished} {unpublished === 1 ? 'block has' : 'blocks have'} unpublished changes
                </Badge>
              )}
            </div>

            <ul className="outline-list outline-list--root">
              {sections.map((section, index) => (
                <OutlineNode
                  key={section.id}
                  block={section}
                  parentLocator={root.id}
                  index={index}
                  siblingCount={sections.length}
                  label={childInfo?.display_name ?? section.category}
                  ops={ops}
                />
              ))}
            </ul>

            {childInfo && root.actions.childAddable && (
              <button
                type="button"
                className="outline-add outline-add--root"
                onClick={() => ops.addChild(root.id, childInfo.category, childInfo.display_name)}
              >
                <Icon name="add" />
                New {childInfo.display_name.toLowerCase()}
              </button>
            )}
          </>
        )}
      </Card>

      {configuring && (
        <ConfigureDialog
          block={configuring.block}
          childLabel={configuring.label}
          busy={act.isPending}
          error={act.isError ? act.error : null}
          onClose={() => setConfiguring(null)}
          onSave={(settings: BlockSettings) =>
            run(configuring.block.id, 'The settings were not saved.', () =>
              updateBlockSettings(configuring.block.id, settings),
            )
          }
        />
      )}

      {confirm?.kind === 'delete' && (
        <ConfirmDialog
          title={`Delete this ${confirm.label.toLowerCase()}?`}
          confirmLabel={`Delete ${confirm.label.toLowerCase()}`}
          busy={act.isPending}
          onClose={() => setConfirm(null)}
          onConfirm={() =>
            run(confirm.block.id, `The ${confirm.label.toLowerCase()} was not deleted.`, () =>
              deleteBlock(confirm.block.id),
            )
          }
          body={
            <>
              <p>
                <strong>{confirm.block.display_name}</strong> and everything inside it will be
                removed. This cannot be undone.
              </p>
              {confirm.block.published && (
                <Banner tone="warning">
                  This is published, so learners can see it now. Deleting it removes it from the
                  live course as well, along with any work they have done in it.
                </Banner>
              )}
            </>
          }
        />
      )}

      {confirm?.kind === 'discard' && (
        <ConfirmDialog
          title="Discard unpublished changes?"
          confirmLabel="Discard changes"
          busy={act.isPending}
          onClose={() => setConfirm(null)}
          onConfirm={() =>
            run(confirm.block.id, 'The changes were not discarded.', () =>
              discardChanges(confirm.block.id),
            )
          }
          body={
            <p>
              <strong>{confirm.block.display_name}</strong> will go back to the version learners
              currently see. Edits made since it was last published will be lost.
            </p>
          }
        />
      )}
    </>
  );
}

/** Every locator in the tree, for "expand all". */
function allLocators(root: OutlineBlock, into: string[] = []): string[] {
  for (const child of root.child_info?.children ?? []) {
    into.push(child.id);
    allLocators(child, into);
  }
  return into;
}

/** The server's label is singular; a count of one should not read "1 Sections". */
function plural(label: string, count: number): string {
  return count === 1 ? label : `${label}s`;
}
